import React, { useState } from 'react';
import { Box } from '@mui/material';
import AvatarImg from 'pages/components/avatarImg';
import { getUrl,groupImgUrl } from 'utilities/userImageURL';
import TypographyWithI18N from 'pages/components/TypographyWithI18N';
import { colors } from 'styles/colors';
import { useNavigate } from 'react-router-dom';
import { buildSlugId, getPublicId } from 'utilities/helpers';
import { ApiPaths, ApiPaths_Groups, IGuestModeOptions, ITrendingUser } from 'models/apiPaths';
import SigninModal from 'pages/components/signinModal';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { RootState } from 'store';
import { setGroupNameData } from 'store/utilsSlice';
import { axiosGetGroupPost, fetchUserDetails } from 'services/api';

interface IActiveUsersProps {
  activeUsersList: ITrendingUser[];
  isLoggedIn?: boolean;
}

const ActiveUsers = ({ activeUsersList, isLoggedIn }: IActiveUsersProps) => {
  const dispatch = useAppDispatch();
  const { blockedUserIds }: any = useAppSelector(
    (state: RootState) => state.utilsData
  );
  const [guestModeOptions, setGuestModeOptions] = useState<IGuestModeOptions>({
    guestMode: false,
    navigateTo: null,
    menu: null,
    redirectTo: null,
    action: null,
    actionItem: null
  });
  const [openSigninDialog, setOpenSigninDialog] = useState<boolean>(false);
  const navigate = useNavigate();

  const filteredUsers = (activeUsersList || []).filter(
    (item) => !blockedUserIds?.includes(item.id)
  );

  return (
    <Box
      id='active_userList'
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        paddingX: 5,
         width: '100%',      
         boxSizing: 'border-box'  
      }}
    >
      {filteredUsers.map((item, index) => {
        const imageId = item.type === 'GROUP' ? item.groupImage?.id ?? undefined : item.userImage?.id ?? undefined;
        // The trending endpoint can return a group directly or inside a group
        // wrapper. Resolve the same group entity used by Joined Groups.
        const group = (item as any).group || item;
        const targetEntity = item.type === 'GROUP' ? group : item;
        const targetId = item.type === 'GROUP' ? (group?.id ?? item.id) : item.id;
        const groupHeaderName = item?.name || group?.name || group?.groupName || '';

        return (
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          key={index.toString()}
          onClick={async () => {
            let routeId = (item as any)?.publicId || (item as any)?.public || (group as any)?.publicId || (group as any)?.public;
            let targetPath = '';

            if (item.type === 'GROUP') {
              dispatch(
                setGroupNameData({
                  groupNameData: groupHeaderName
                })
              );
              if (!routeId) {
                try {
                  const fetchedGroup = await axiosGetGroupPost(targetId, ApiPaths_Groups.GROUP_POST);
                  routeId = fetchedGroup?.publicId || fetchedGroup?.public || targetId;
                } catch {
                  routeId = targetId;
                }
              }
              targetPath = `/group/${routeId}`;
            } else {
              let userEntity: any = item;
              if (!routeId || routeId === item.id) {
                try {
                  const apiPath = `${ApiPaths.GET_USER}/${item.id}`;
                  const userDetail: any = await fetchUserDetails(apiPath);
                  if (userDetail) {
                    userEntity = userDetail;
                    routeId = userDetail?.publicId || userDetail?.public || item.id;
                  }
                } catch {
                  routeId = routeId || getPublicId(targetEntity, targetId);
                }
              }
              const userSlugName = userEntity?.summary?.userName || userEntity?.username || item?.name;
              targetPath = `/profile/${buildSlugId(userSlugName, routeId)}`;
            }

            if (isLoggedIn) {
               navigate(
                  targetPath,
                  item.type === 'GROUP' ? { state: { headerName: groupHeaderName } } : undefined
                );
            } else {
              setGuestModeOptions({
                guestMode: true,
                navigateTo: item.type === 'GROUP' ? 'group' : 'profile',
                menu: item.type === 'GROUP' ? 'group' : 'profile',
                redirectTo: targetPath,
                action: item.type === 'GROUP' ? 'group' : 'profile',
                actionItem: null
              });
              setOpenSigninDialog(true);
            }
          }}
        >
          <AvatarImg
            id='userImg'
            enableLetterImg={imageId ? false : true}
            alt={item?.name}
            src={imageId ? (item.type === 'GROUP' ? groupImgUrl(imageId) : getUrl(imageId)) : ''}
            name={item?.name}
            nameStyle={{
              fontSize: 18
            }}
            width={66}
            height={66}
            style={{
              objectFit: 'cover',
              borderRadius: '50%'
            }}
          />
          <TypographyWithI18N
            id='title'
            className='ellipsisText'
            maxWidth={50}
            sx={{
              color: colors.text.primaryBlack
            }}
            i18nKey={item?.name}
            tabIndex={0}
          />
        </Box>
        );
      })}
      {(
        openSigninDialog && (
          <SigninModal
            guestModeOptions={guestModeOptions}
            openSigninDialog={openSigninDialog}
            closeSigninDialog={() => {setOpenSigninDialog(false)}}
          />
        )
      )}
    </Box>
  );
};
 
export default ActiveUsers;
